import React, { useMemo, useState } from "react";
import { Calculator, IndianRupee, Percent, Calendar, User } from "lucide-react";
import ToolCard from "./ToolCard";
import { formatINR } from "./formatters";

// Simple retirement corpus approximation:
// Future monthly expense = currentExpense * (1+inflation)^(yearsToRetire)
// Required corpus ~ annualExpenseAtRetire * multiplier (e.g. 25x)  [roughly 4% rule]
function retirementEstimate(currentMonthlyExpense, inflation, yearsToRetire, multiplier) {
  const E = Number(currentMonthlyExpense);
  const inf = Number(inflation) / 100;
  const y = Number(yearsToRetire);
  const m = Number(multiplier);

  if (E <= 0 || y < 0) return { monthlyAtRetire: 0, corpus: 0, annualAtRetire: 0 };
  const monthlyAtRetire = E * Math.pow(1 + inf, y);
  const annualAtRetire = monthlyAtRetire * 12;
  const corpus = annualAtRetire * m;
  return { monthlyAtRetire, annualAtRetire, corpus };
}

export default function RetirementPlanner() {
  const [age, setAge] = useState(30);
  const [retAge, setRetAge] = useState(60);
  const [expense, setExpense] = useState(50000);
  const [inflation, setInflation] = useState(6);
  const [multiplier, setMultiplier] = useState(25);

  const yearsToRetire = useMemo(() => Math.max(0, Number(retAge) - Number(age)), [age, retAge]);
  const { monthlyAtRetire, annualAtRetire, corpus } = useMemo(
    () => retirementEstimate(expense, inflation, yearsToRetire, multiplier),
    [expense, inflation, yearsToRetire, multiplier]
  );

  return (
    <ToolCard
      title="Retirement Planner"
      subtitle="Estimate retirement expense and target corpus using inflation and a corpus multiplier."
    >
      <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-950">
        <Calculator className="h-4 w-4" />
        Retirement planning
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <Field label="Your age" icon={<User className="h-4 w-4 text-slate-500" />} value={age} onChange={setAge} min={0} />
        <Field
          label="Retirement age"
          icon={<Calendar className="h-4 w-4 text-slate-500" />}
          value={retAge}
          onChange={setRetAge}
          min={0}
        />
        <Field
          label="Monthly expense today"
          icon={<IndianRupee className="h-4 w-4 text-slate-500" />}
          value={expense}
          onChange={setExpense}
          min={0}
        />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field
          label="Inflation (p.a.)"
          icon={<Percent className="h-4 w-4 text-slate-500" />}
          value={inflation}
          onChange={setInflation}
          min={0}
          step="0.1"
        />
        <Field
          label="Corpus multiplier"
          icon={<Calculator className="h-4 w-4 text-slate-500" />}
          value={multiplier}
          onChange={setMultiplier}
          min={1}
          step="1"
        />
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <ResultBox title="Years to retire" value={`${yearsToRetire} years`} />
        <ResultBox title="Monthly expense at retirement" value={formatINR(monthlyAtRetire)} />
        <ResultBox title="Target corpus" value={formatINR(corpus)} highlight />
      </div>

      <p className="mt-3 text-xs text-slate-500">
        Note: This is a simplified estimate. A full plan considers returns, taxes, healthcare, and lifestyle changes.
      </p>
    </ToolCard>
  );
}

function Field({ label, icon, value, onChange, min = 0, step = "1" }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-700">{label}</label>
      <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
        {icon}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full text-sm outline-none"
          min={min}
          step={step}
        />
      </div>
    </div>
  );
}

function ResultBox({ title, value, highlight }) {
  return (
    <div className={`rounded-2xl p-4 ${highlight ? "bg-emerald-50" : "bg-slate-50"}`}>
      <div className={`text-xs ${highlight ? "text-emerald-900/70" : "text-slate-600"}`}>{title}</div>
      <div className={`mt-1 text-lg font-semibold ${highlight ? "text-emerald-950" : "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}
